import { useEffect } from 'react';

type LenisInstance = { raf: (time: number) => void; destroy: () => void };

export default function SmoothScroll() {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return undefined;

    let lenis: LenisInstance | null = null;
    let frameId = 0;
    let cancelled = false;

    const start = async () => {
      const { default: Lenis } = await import('lenis');
      if (cancelled) return;
      lenis = new Lenis({
        duration: 1.1,
        easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        wheelMultiplier: 1,
        touchMultiplier: 1.6,
      });
      const raf = (time: number) => {
        lenis?.raf(time);
        frameId = requestAnimationFrame(raf);
      };
      frameId = requestAnimationFrame(raf);
    };

    // Lance Lenis seulement quand le navigateur est idle (= après FCP/LCP)
    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    let idleHandle: number | null = null;
    let timeoutHandle: number | null = null;
    if (typeof idle === 'function') {
      idleHandle = idle(start, { timeout: 1500 });
    } else {
      timeoutHandle = window.setTimeout(start, 200);
    }

    return () => {
      cancelled = true;
      if (idleHandle !== null) {
        const cancelIdle = (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback;
        cancelIdle?.(idleHandle);
      }
      if (timeoutHandle !== null) clearTimeout(timeoutHandle);
      cancelAnimationFrame(frameId);
      lenis?.destroy();
    };
  }, []);

  return null;
}
